/**
 * Chart Setup
 * 
 * Handles chart initialization, DOM setup, and configuration.
 */

import type { ChartOptions, AxisOptions } from "../../types";
import { parseColor } from "../../renderer/NativeWebGLRenderer";
import { LinearScale, LogScale, type Scale } from "../../scales";
import { DEFAULT_THEME, getThemeByName, type ChartTheme } from "../../theme";
import { MARGINS } from "./types";
import { mergeLayoutOptions, type LayoutOptions } from "../layout";

export interface SetupResult {
  theme: ChartTheme;
  backgroundColor: [number, number, number, number];
  plotAreaColor: [number, number, number, number];
  showLegend: boolean;
  showControls: boolean;
  autoScroll: boolean;
  showStatistics: boolean;
  dpr: number;
  xAxisOptions: AxisOptions;
  xScale: Scale;
  yAxisOptionsMap: Map<string, AxisOptions>;
  yScales: Map<string, Scale>;
  primaryYAxisId: string;
  webglCanvas: HTMLCanvasElement;
  overlayCanvas: HTMLCanvasElement;
  overlayCtx: CanvasRenderingContext2D;
  svgRoot: SVGSVGElement | null;
  layout: LayoutOptions;
}

/**
 * Initialize chart configuration from options
 */
export function initializeChart(
  container: HTMLDivElement,
  options: ChartOptions
): SetupResult {
  if (!container) throw new Error("[VeloPlot] Container element is required");

  const dpr = options.devicePixelRatio ?? window.devicePixelRatio;

  // Initialize layout
  const layout = mergeLayoutOptions(options.layout);

  // Initialize theme
  const theme = typeof options.theme === "string"
    ? getThemeByName(options.theme)
    : (options.theme as ChartTheme) ?? DEFAULT_THEME;

  const bgColor = parseColor(options.background ?? theme.backgroundColor);
  const backgroundColor: [number, number, number, number] = [bgColor[0], bgColor[1], bgColor[2], bgColor[3]];

  const paColor = parseColor(theme.plotAreaBackground);
  const plotAreaColor: [number, number, number, number] = [paColor[0], paColor[1], paColor[2], paColor[3]];

  const useSvgRenderer = options.renderer === "svg";
  const showLegend =
    options.showLegend ?? (useSvgRenderer ? false : theme.legend.visible);
  const showControls = options.toolbar?.show ?? options.showControls ?? false;
  const autoScroll = options.autoScroll ?? false;
  const showStatistics = options.showStatistics ?? false;

  // X Axis
  const xAxisOptions: AxisOptions = { scale: "linear", auto: true, ...options.xAxis };
  const xScale = xAxisOptions.scale === "log" ? new LogScale() : new LinearScale();

  // Process Y Axes
  const yAxisOptionsMap = new Map<string, AxisOptions>();
  const yScales = new Map<string, Scale>();
  let primaryYAxisId = 'default';

  const providedYAxes = options.yAxis
    ? (Array.isArray(options.yAxis) ? options.yAxis : [options.yAxis])
    : [{}];

  providedYAxes.forEach((axisOpt, index) => {
    const isFirst = index === 0;
    const defaultId = isFirst ? 'default' : `y${index}`;
    const id = axisOpt.id || defaultId;

    if (isFirst) primaryYAxisId = id;
    const position = axisOpt.position || (isFirst ? 'left' : 'right');
    const fullOptions: AxisOptions = { scale: "linear", auto: true, position, ...axisOpt, id };

    yAxisOptionsMap.set(id, fullOptions);
    yScales.set(id, fullOptions.scale === "log" ? new LogScale() : new LinearScale());
  });

  // Create DOM structure. Preserve absolute fill used by stacked panes —
  // overwriting with `relative` collapses height (absolute canvases don't
  // contribute) and breaks pane resize / shared-axis layouts.
  if (container.style.position !== "absolute") {
    container.style.position = "relative";
  }
  container.style.overflow = "hidden";
  container.style.backgroundColor = options.background ?? theme.backgroundColor;

  const webglCanvas = createCanvas("webgl");
  const overlayCanvas = createCanvas("overlay");

  const svgRoot = useSvgRenderer ? createSvgRoot() : null;

  while (container.firstChild) {
    container.removeChild(container.firstChild);
  }

  container.appendChild(webglCanvas);
  container.appendChild(overlayCanvas);

  const ctx = overlayCanvas.getContext("2d");
  if (!ctx) throw new Error("Failed to get 2D context");

  if (useSvgRenderer && svgRoot) {
    container.insertBefore(svgRoot, webglCanvas);
    webglCanvas.style.display = "none";
    overlayCanvas.style.display = "block";
    overlayCanvas.style.pointerEvents = "none";
    svgRoot.style.zIndex = "1";
    overlayCanvas.style.zIndex = "2";
  }

  return {
    theme,
    backgroundColor,
    plotAreaColor,
    showLegend,
    showControls,
    autoScroll,
    showStatistics,
    dpr,
    xAxisOptions,
    xScale,
    yAxisOptionsMap,
    yScales,
    primaryYAxisId,
    webglCanvas,
    overlayCanvas,
    overlayCtx: ctx,
    svgRoot,
    layout,
  };
}

function createSvgRoot(): SVGSVGElement {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("class", "velo-plot-svg-layer");
  svg.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  svg.style.position = "absolute";
  svg.style.inset = "0";
  svg.style.width = "100%";
  svg.style.height = "100%";
  svg.style.pointerEvents = "none";
  return svg;
}

/**
 * Create a canvas element
 */
export function createCanvas(type: "webgl" | "overlay"): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.style.cssText = "position:absolute;top:0;left:0;width:100%;height:100%;";
  if (type === "overlay") canvas.style.pointerEvents = "none";
  return canvas;
}

/**
 * Calculate the plot area based on container size and margins
 */
export function getPlotArea(
  container: HTMLDivElement,
  yAxisOptionsMap: Map<string, AxisOptions>,
  layout?: LayoutOptions
): { x: number; y: number; width: number; height: number } {
  const rect = container.getBoundingClientRect();
  const leftAxisCount = Array.from(yAxisOptionsMap.values()).filter(a => a.position !== 'right').length;
  const rightAxisCount = Array.from(yAxisOptionsMap.values()).filter(a => a.position === 'right').length;

  const margins = layout?.margins || MARGINS;
  const leftMargin = (margins.left ?? MARGINS.left) + Math.max(0, leftAxisCount - 1) * 65;
  const rightMargin = (margins.right ?? MARGINS.right) + rightAxisCount * 65;
  const topMargin = margins.top ?? MARGINS.top;
  const bottomMargin = margins.bottom ?? MARGINS.bottom;

  return {
    x: leftMargin,
    y: topMargin,
    width: Math.max(1, rect.width - leftMargin - rightMargin),
    height: Math.max(1, rect.height - topMargin - bottomMargin),
  };
}

/**
 * Get axes layout for interaction manager
 */
export function getAxesLayout(
  yAxisOptionsMap: Map<string, AxisOptions>
): Array<{ id: string; position: 'left' | 'right'; offset: number }> {
  // Separate axes by position to calculate offset correctly
  const result: Array<{ id: string; position: 'left' | 'right'; offset: number }> = [];
  let leftIndex = 0;
  let rightIndex = 0;

  yAxisOptionsMap.forEach((opts, id) => {
    const position = (opts.position === 'right' ? 'right' : 'left') as 'left' | 'right';
    const offset = position === 'left' ? leftIndex * 65 : rightIndex * 65;

    if (position === 'left') leftIndex++;
    else rightIndex++;

    result.push({ id, position, offset });
  });

  return result;
}

/**
 * Resize canvases to match container
 */
export function resizeCanvases(
  container: HTMLDivElement,
  webglCanvas: HTMLCanvasElement,
  overlayCanvas: HTMLCanvasElement,
  overlayCtx: CanvasRenderingContext2D,
  dpr: number
): boolean {
  const rect = container.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) return false;

  // Integer backing-store pixels avoid blurry half-pixel scaling.
  const w = Math.max(1, Math.round(rect.width * dpr));
  const h = Math.max(1, Math.round(rect.height * dpr));
  const cssW = `${Math.round(rect.width)}px`;
  const cssH = `${Math.round(rect.height)}px`;

  const webglNeedsResize =
    webglCanvas.width !== w ||
    webglCanvas.height !== h ||
    webglCanvas.style.width !== cssW ||
    webglCanvas.style.height !== cssH;

  const overlayNeedsResize =
    overlayCanvas.width !== w || overlayCanvas.height !== h;

  if (!webglNeedsResize && !overlayNeedsResize) {
    // Keep transform in sync even when backing-store dimensions are unchanged.
    overlayCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
    overlayCtx.imageSmoothingEnabled = true;
    if ("imageSmoothingQuality" in overlayCtx) {
      overlayCtx.imageSmoothingQuality = "high";
    }
    return false;
  }

  if (webglNeedsResize) {
    webglCanvas.width = w;
    webglCanvas.height = h;
    webglCanvas.style.width = cssW;
    webglCanvas.style.height = cssH;
  }

  if (overlayNeedsResize) {
    overlayCanvas.width = w;
    overlayCanvas.height = h;
  }

  // Keep overlay CSS dimensions aligned even when only the backing store changed.
  overlayCanvas.style.width = cssW;
  overlayCanvas.style.height = cssH;

  // Must run AFTER any canvas dimension assignment — changing width/height resets
  // the 2D context transform back to identity.
  overlayCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
  overlayCtx.imageSmoothingEnabled = true;
  if ("imageSmoothingQuality" in overlayCtx) {
    overlayCtx.imageSmoothingQuality = "high";
  }

  return true;
}

export function pixelToDataX(
  px: number,
  plotArea: { x: number, width: number },
  viewBounds: { xMin: number, xMax: number },
  invertAxis = false,
): number {
  const normalized = (px - plotArea.x) / plotArea.width;
  const xMin = invertAxis ? viewBounds.xMax : viewBounds.xMin;
  const xMax = invertAxis ? viewBounds.xMin : viewBounds.xMax;
  return xMin + normalized * (xMax - xMin);
}

export function pixelToDataY(
  py: number,
  plotArea: { y: number, height: number },
  viewBounds: { yMin: number, yMax: number },
  invertAxis = false,
): number {
  const normalized = (py - plotArea.y) / plotArea.height;
  const yMin = invertAxis ? viewBounds.yMin : viewBounds.yMax;
  const yMax = invertAxis ? viewBounds.yMax : viewBounds.yMin;
  return yMin + normalized * (yMax - yMin);
}
